import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryErrorResetBoundary,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { initGtm } from "@/lib/gtm";
import { captureAttributionFromUrl } from "@/lib/attribution";
import { DirectionProvider } from "./context/direction-provider";
import { FontProvider } from "./context/font-provider";
import { ThemeProvider } from "./context/theme-provider";
import { I18nProvider } from "./context/i18n-provider";
import { PostHogProvider } from "posthog-js/react";
// Generated Routes
import { routeTree } from "./routeTree.gen";
// Styles
import "./styles/index.css";
import "sonner/dist/styles.css";
import { AuthProvider } from "./context/auth-provider";
import { AppErrorBoundary } from "./components/error-boundary";
import { GlobalPending } from "./components/global-pending";
import { reloadOnce } from "@/lib/safe-reload";

// Build identifier injected by Vite (see vite.config.ts `define`), compared
// against /version.json to detect a stale shell after a deploy.
declare const __BUILD_VERSION__: string;

// Handle chunk load failures gracefully after deployment: when stale cached HTML
// references chunks that no longer exist, refresh once to get fresh assets
// (official Vite recommendation: https://vite.dev/guide/build). `reloadOnce`
// guards against an infinite reload loop if the reload keeps serving a stale
// index (e.g. iOS Safari / installed PWA) — after that, the error surfaces.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnce()) event.preventDefault();
});

// iOS Safari restores a days-stale app shell from bfcache / an installed-PWA
// snapshot without firing focus, so use-version-check's refetch-on-focus misses
// it — the stale shell then imports a now-deleted chunk and breaks. On a
// persisted pageshow (the canonical bfcache-restore signal), verify the build is
// current and swap proactively before any navigation hits a dead chunk.
window.addEventListener("pageshow", (e) => {
  if (!(e as PageTransitionEvent).persisted) return;
  fetch("/version.json", { cache: "no-store" })
    .then((r) => r.json() as Promise<{ version: string }>)
    .then((d) => {
      if (d?.version && d.version !== __BUILD_VERSION__) reloadOnce();
    })
    .catch(() => {});
});

// Persist ad-click attribution (UTMs + fbclid) from the landing URL before
// passwordless auth navigation drops the query params. First-touch: stored once
// and read back at sign-in (see auth-provider).
captureAttributionFromUrl();

// Create a new router instance first (needed for Sentry integration)
const router = createRouter({
  routeTree,
  context: {
    queryClient: {} as QueryClient, // Temporary - will be set properly below
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: GlobalPending,
  defaultPendingMs: 0,
  defaultPendingMinMs: 100,
});

// Initialize Sentry as early as possible with modern TanStack Router integration
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.01 : 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,
    environment: import.meta.env.MODE,
  });
}

// Initialize Google Tag Manager (prod only, to keep dev/preview traffic out of
// Google Ads / Meta Ads conversion data). Tags are configured in the GTM UI.
if (import.meta.env.PROD && import.meta.env.VITE_GTM_CONTAINER_ID) {
  initGtm(import.meta.env.VITE_GTM_CONTAINER_ID);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Development logging for debugging queries
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log({ failureCount, error });
        }

        if (failureCount >= 0 && import.meta.env.DEV) return false;
        return failureCount <= 3;
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      // Report each query error to Sentry exactly once. Sentry timestamps
      // events itself, so we only attach the current URL for context.
      if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.captureException(error, {
          tags: { context: "query" },
          extra: { url: router.history.location.href },
        });
      }
    },
  }),
});

// Update router context with the actual queryClient
router.update({
  context: {
    queryClient,
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
      }}
    />
  );
}

// Render the app
const rootElement = document.getElementById("root")!;
if (rootElement.querySelector("#initial-loader")) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          // Analytics/replay go through our first-party proxy (api_host); the
          // toolbar and links still need PostHog's real UI origin.
          ui_host: "https://eu.posthog.com",
          defaults: "2025-11-30",
          capture_exceptions: import.meta.env.PROD,
          debug: import.meta.env.MODE === "development",
          opt_out_capturing_by_default: !import.meta.env.PROD,
          // Support chat is Crisp; this skips PostHog's chat script at startup.
          disable_conversations: true,
          session_recording: {
            maskAllInputs: false,
            maskInputOptions: {
              password: true,
            },
          },
        }}
      >
        <AppErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <QueryErrorResetBoundary>
              {({ reset }) => (
                <AppErrorBoundary onReset={reset}>
                  <ThemeProvider>
                    <FontProvider>
                      <DirectionProvider>
                        <I18nProvider>
                          <AuthProvider>
                            <App />
                          </AuthProvider>
                        </I18nProvider>
                      </DirectionProvider>
                    </FontProvider>
                  </ThemeProvider>
                </AppErrorBoundary>
              )}
            </QueryErrorResetBoundary>
          </QueryClientProvider>
        </AppErrorBoundary>
      </PostHogProvider>
    </StrictMode>,
  );
}
